import * as i0 from '@angular/core';
import { InjectionToken, inject, signal, input, booleanAttribute, computed, contentChild, Component, ChangeDetectionStrategy, ElementRef, HostListener, NgModule } from '@angular/core';
const _c0 = [[["", "uxa-avatar-image", ""]]];
const _c1 = ["[uxa-avatar-image]"];
function UxaAvatarComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 0);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.initials());
  }
}
const _c2 = ["uxa-avatar-image", ""];
const _c3 = ["*"];
const UxaAvatarImageToken = new InjectionToken('UxaAvatarImageToken');
/**
 * Provide the avatar image component.
 */
function injectAvatarImage() {
  return inject(UxaAvatarImageToken);
}
const defaultAvatarConfig = {
  size: 'medium',
  color: 'raspberry'
};
const UxaAvatarConfigToken = new InjectionToken('UxaAvatarConfigToken');
/**
 * Provide the avatar config
 * @param config The avatar config
 * @returns The provider
 */
function provideAvatarConfig(config) {
  return [{
    provide: UxaAvatarConfigToken,
    useValue: {
      ...defaultAvatarConfig,
      ...config
    }
  }];
}
/**
 * Inject the avatar config
 * @returns The global avatar config
 */
function injectAvatarConfig() {
  return inject(UxaAvatarConfigToken, {
    optional: true
  }) ?? defaultAvatarConfig;
}
const UxaAvatarToken = new InjectionToken('UxaAvatarToken');
/**
 * Provide the avatar component.
 */
function injectAvatar() {
  return inject(UxaAvatarToken);
}
class UxaAvatarComponent {
  constructor() {
    /**
     * The global avatar configuration.
     */
    this._config = injectAvatarConfig();
    /**
     * Store the current state of the avatar.
     * @internal
     */
    this.state = signal(AvatarState.Idle);
    /**
     * Determine the size of the avatar.
     */
    this.size = input(this._config.size);
    /**
     * Determine the color of the avatar.
     */
    this.color = input(this._config.color);
    /**
     * Determine the initials to display.
     */
    this.initials = input();
    /**
     * Whether the avatar should have a border.
     */
    this.bordered = input(false, {
      transform: booleanAttribute
    });
    /**
     * Whether the avatar should be interactive.
     */
    this.interactive = input(false, {
      transform: booleanAttribute
    });
    /**
     * Determine whether we should show the initials.
     */
    this.showInitials = computed(() => !this._image() ? true : this.state() !== AvatarState.Loaded);
    /**
     * Determine if there is an image in the avatar.
     */
    this._image = contentChild(UxaAvatarImageToken);
  }
  /**
   * Set the avatar state.
   * @param state The state to set.
   * @internal
   */
  setState(state) {
    this.state.set(state);
  }
  static {
    this.ɵfac = function UxaAvatarComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAvatarComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaAvatarComponent,
      selectors: [["uxa-avatar"]],
      contentQueries: function UxaAvatarComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuerySignal(dirIndex, ctx._image, UxaAvatarImageToken, 5);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance();
        }
      },
      hostVars: 28,
      hostBindings: function UxaAvatarComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("tabindex", ctx.interactive() ? 0 : undefined)("role", ctx.interactive() ? "button" : undefined);
          i0.ɵɵclassProp("uxa-extra-small", ctx.size() === "extra-small")("uxa-small", ctx.size() === "small")("uxa-medium", ctx.size() === "medium")("uxa-large", ctx.size() === "large")("uxa-raspberry", ctx.color() === "raspberry")("uxa-plum", ctx.color() === "plum")("uxa-blue", ctx.color() === "blue")("uxa-teal", ctx.color() === "teal")("uxa-cerulean", ctx.color() === "cerulean")("uxa-navy", ctx.color() === "navy")("uxa-steel", ctx.color() === "steel")("uxa-bordered", ctx.bordered())("uxa-interactive", ctx.interactive());
        }
      },
      inputs: {
        size: [1, "size"],
        color: [1, "color"],
        initials: [1, "initials"],
        bordered: [1, "bordered"],
        interactive: [1, "interactive"]
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: UxaAvatarToken,
        useExisting: UxaAvatarComponent
      }])],
      ngContentSelectors: _c1,
      decls: 2,
      vars: 1,
      consts: [[1, "uxa-avatar-initials"]],
      template: function UxaAvatarComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c0);
          i0.ɵɵtemplate(0, UxaAvatarComponent_Conditional_0_Template, 2, 1, "span", 0);
          i0.ɵɵprojection(1);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.showInitials() ? 0 : -1);
        }
      },
      styles: ["[_nghost-%COMP%], .uxa-avatar[_ngcontent-%COMP%]{display:inline-flex;justify-content:center;align-items:center;border-radius:var(--uxa-avatar__border-radius);cursor:default;color:var(--uxa-avatar__color);box-sizing:border-box}.uxa-bordered[_nghost-%COMP%], .uxa-avatar.uxa-bordered[_ngcontent-%COMP%]{border:var(--uxa-avatar__border-width) solid var(--uxa-avatar__border-color--bordered)}.uxa-interactive[_nghost-%COMP%]:hover, .uxa-avatar.uxa-interactive[_ngcontent-%COMP%]:hover{box-shadow:0 0 0 var(--uxa-avatar__border-width) var(--uxa-avatar__border-color--hover)}.uxa-interactive[_nghost-%COMP%]:active, .uxa-avatar.uxa-interactive[_ngcontent-%COMP%]:active{box-shadow:0 0 0 var(--uxa-avatar__border-width) var(--uxa-avatar__border-color--active)}[_nghost-%COMP%]:focus-visible, .uxa-focused[_nghost-%COMP%], .uxa-avatar[_ngcontent-%COMP%]:focus-visible, .uxa-avatar.uxa-focused[_ngcontent-%COMP%]{outline:var(--uxa-avatar__outline--focused);outline-offset:var(--uxa-avatar__outline-offset--focused)}.uxa-extra-small[_nghost-%COMP%], .uxa-avatar.uxa-extra-small[_ngcontent-%COMP%]{width:var(--uxa-avatar__size--xs);height:var(--uxa-avatar__size--xs);font-size:var(--uxa-avatar__font-size--xs);line-height:var(--uxa-avatar__line-height--xs);font-weight:var(--uxa-avatar__font-weight--xs);--uxa-avatar__border-width: var(--uxa-avatar__border-width--xs)}.uxa-small[_nghost-%COMP%], .uxa-avatar.uxa-small[_ngcontent-%COMP%]{width:var(--uxa-avatar__size--sm);height:var(--uxa-avatar__size--sm);font-size:var(--uxa-avatar__font-size--sm);line-height:var(--uxa-avatar__line-height--sm);font-weight:var(--uxa-avatar__font-weight--sm);--uxa-avatar__border-width: var(--uxa-avatar__border-width--sm)}.uxa-medium[_nghost-%COMP%], .uxa-avatar.uxa-medium[_ngcontent-%COMP%]{width:var(--uxa-avatar__size--md);height:var(--uxa-avatar__size--md);font-size:var(--uxa-avatar__font-size--md);line-height:var(--uxa-avatar__line-height--md);font-weight:var(--uxa-avatar__font-weight--md);--uxa-avatar__border-width: var(--uxa-avatar__border-width--md)}.uxa-large[_nghost-%COMP%], .uxa-avatar.uxa-large[_ngcontent-%COMP%]{width:var(--uxa-avatar__size--lg);height:var(--uxa-avatar__size--lg);font-size:var(--uxa-avatar__font-size--lg);line-height:var(--uxa-avatar__line-height--lg);font-weight:var(--uxa-avatar__font-weight--lg);--uxa-avatar__border-width: var(--uxa-avatar__border-width--lg)}.uxa-raspberry[_nghost-%COMP%], .uxa-avatar.uxa-raspberry[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-pink)}.uxa-plum[_nghost-%COMP%], .uxa-avatar.uxa-plum[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-purple)}.uxa-blue[_nghost-%COMP%], .uxa-avatar.uxa-blue[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-blue)}.uxa-cerulean[_nghost-%COMP%], .uxa-avatar.uxa-cerulean[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-cerulean)}.uxa-teal[_nghost-%COMP%], .uxa-avatar.uxa-teal[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-teal)}.uxa-navy[_nghost-%COMP%], .uxa-avatar.uxa-navy[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-navy)}.uxa-steel[_nghost-%COMP%], .uxa-avatar.uxa-steel[_ngcontent-%COMP%]{background-color:var(--jato-avatar-background-steel)}[_nghost-%COMP%]:has(.uxa-avatar-image), [_nghost-%COMP%]:has([uxa-avatar-image]), .uxa-avatar[_ngcontent-%COMP%]:has(.uxa-avatar-image), .uxa-avatar[_ngcontent-%COMP%]:has([uxa-avatar-image]){background-color:transparent}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAvatarComponent, [{
    type: Component,
    args: [{
      standalone: true,
      selector: 'uxa-avatar',
      changeDetection: ChangeDetectionStrategy.OnPush,
      providers: [{
        provide: UxaAvatarToken,
        useExisting: UxaAvatarComponent
      }],
      host: {
        '[class.uxa-extra-small]': "size() === 'extra-small'",
        '[class.uxa-small]': "size() === 'small'",
        '[class.uxa-medium]': "size() === 'medium'",
        '[class.uxa-large]': "size() === 'large'",
        '[class.uxa-raspberry]': "color() === 'raspberry'",
        '[class.uxa-plum]': "color() === 'plum'",
        '[class.uxa-blue]': "color() === 'blue'",
        '[class.uxa-teal]': "color() === 'teal'",
        '[class.uxa-cerulean]': "color() === 'cerulean'",
        '[class.uxa-navy]': "color() === 'navy'",
        '[class.uxa-steel]': "color() === 'steel'",
        '[class.uxa-bordered]': 'bordered()',
        '[class.uxa-interactive]': 'interactive()',
        '[attr.tabindex]': 'interactive() ? 0 : undefined',
        '[attr.role]': 'interactive() ? "button" : undefined'
      },
      template: "@if (showInitials()) {\n  <span class=\"uxa-avatar-initials\">{{ initials() }}</span>\n}\n\n<ng-content select=\"[uxa-avatar-image]\" />\n",
      styles: [":host,.uxa-avatar{display:inline-flex;justify-content:center;align-items:center;border-radius:var(--uxa-avatar__border-radius);cursor:default;color:var(--uxa-avatar__color);box-sizing:border-box}:host.uxa-bordered,.uxa-avatar.uxa-bordered{border:var(--uxa-avatar__border-width) solid var(--uxa-avatar__border-color--bordered)}:host.uxa-interactive:hover,.uxa-avatar.uxa-interactive:hover{box-shadow:0 0 0 var(--uxa-avatar__border-width) var(--uxa-avatar__border-color--hover)}:host.uxa-interactive:active,.uxa-avatar.uxa-interactive:active{box-shadow:0 0 0 var(--uxa-avatar__border-width) var(--uxa-avatar__border-color--active)}:host:focus-visible,:host.uxa-focused,.uxa-avatar:focus-visible,.uxa-avatar.uxa-focused{outline:var(--uxa-avatar__outline--focused);outline-offset:var(--uxa-avatar__outline-offset--focused)}:host.uxa-extra-small,.uxa-avatar.uxa-extra-small{width:var(--uxa-avatar__size--xs);height:var(--uxa-avatar__size--xs);font-size:var(--uxa-avatar__font-size--xs);line-height:var(--uxa-avatar__line-height--xs);font-weight:var(--uxa-avatar__font-weight--xs);--uxa-avatar__border-width: var(--uxa-avatar__border-width--xs)}:host.uxa-small,.uxa-avatar.uxa-small{width:var(--uxa-avatar__size--sm);height:var(--uxa-avatar__size--sm);font-size:var(--uxa-avatar__font-size--sm);line-height:var(--uxa-avatar__line-height--sm);font-weight:var(--uxa-avatar__font-weight--sm);--uxa-avatar__border-width: var(--uxa-avatar__border-width--sm)}:host.uxa-medium,.uxa-avatar.uxa-medium{width:var(--uxa-avatar__size--md);height:var(--uxa-avatar__size--md);font-size:var(--uxa-avatar__font-size--md);line-height:var(--uxa-avatar__line-height--md);font-weight:var(--uxa-avatar__font-weight--md);--uxa-avatar__border-width: var(--uxa-avatar__border-width--md)}:host.uxa-large,.uxa-avatar.uxa-large{width:var(--uxa-avatar__size--lg);height:var(--uxa-avatar__size--lg);font-size:var(--uxa-avatar__font-size--lg);line-height:var(--uxa-avatar__line-height--lg);font-weight:var(--uxa-avatar__font-weight--lg);--uxa-avatar__border-width: var(--uxa-avatar__border-width--lg)}:host.uxa-raspberry,.uxa-avatar.uxa-raspberry{background-color:var(--jato-avatar-background-pink)}:host.uxa-plum,.uxa-avatar.uxa-plum{background-color:var(--jato-avatar-background-purple)}:host.uxa-blue,.uxa-avatar.uxa-blue{background-color:var(--jato-avatar-background-blue)}:host.uxa-cerulean,.uxa-avatar.uxa-cerulean{background-color:var(--jato-avatar-background-cerulean)}:host.uxa-teal,.uxa-avatar.uxa-teal{background-color:var(--jato-avatar-background-teal)}:host.uxa-navy,.uxa-avatar.uxa-navy{background-color:var(--jato-avatar-background-navy)}:host.uxa-steel,.uxa-avatar.uxa-steel{background-color:var(--jato-avatar-background-steel)}:host:has(.uxa-avatar-image),:host:has([uxa-avatar-image]),.uxa-avatar:has(.uxa-avatar-image),.uxa-avatar:has([uxa-avatar-image]){background-color:transparent}\n"]
    }]
  }], null, null);
})();
var AvatarState;
(function (AvatarState) {
  AvatarState["Idle"] = "idle";
  AvatarState["Loading"] = "loading";
  AvatarState["Loaded"] = "loaded";
  AvatarState["Error"] = "error";
})(AvatarState || (AvatarState = {}));
class UxaAvatarImageComponent {
  constructor() {
    /** Access the avatar */
    this._avatar = injectAvatar();
    /** Access the image element. */
    this._elementRef = inject(ElementRef);
    /** Determine whether we should show the image */
    this.hidden = computed(() => this._avatar.state() !== AvatarState.Loaded);
  }
  ngOnInit() {
    // mark the avatar as loading
    this._avatar.setState(AvatarState.Loading);
    // if there is no src, we can report this as an error
    if (!this._elementRef.nativeElement.src) {
      this._avatar.setState(AvatarState.Error);
    }
    // if the image has already loaded, we can report this to the avatar
    if (this._elementRef.nativeElement.complete) {
      this._avatar.setState(AvatarState.Loaded);
    }
  }
  onLoad() {
    this._avatar.setState(AvatarState.Loaded);
  }
  onError() {
    this._avatar.setState(AvatarState.Error);
  }
  static {
    this.ɵfac = function UxaAvatarImageComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAvatarImageComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaAvatarImageComponent,
      selectors: [["img", "uxa-avatar-image", ""]],
      hostVars: 2,
      hostBindings: function UxaAvatarImageComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("load", function UxaAvatarImageComponent_load_HostBindingHandler() {
            return ctx.onLoad();
          })("error", function UxaAvatarImageComponent_error_HostBindingHandler() {
            return ctx.onError();
          });
        }
        if (rf & 2) {
          i0.ɵɵclassProp("uxa-hidden", ctx.hidden());
        }
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: UxaAvatarImageToken,
        useExisting: UxaAvatarImageComponent
      }])],
      attrs: _c2,
      ngContentSelectors: _c3,
      decls: 1,
      vars: 0,
      template: function UxaAvatarImageComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      styles: ["[_nghost-%COMP%], .uxa-avatar-image[_ngcontent-%COMP%]{width:100%;height:100%;border-radius:50%}.uxa-hidden[_nghost-%COMP%], .uxa-avatar-image.uxa-hidden[_ngcontent-%COMP%]{border:0;clip:rect(0 0 0 0);height:1px;margin-inline:-1px;margin-block:-1px;overflow:hidden;padding-inline:0;padding-block:0;position:absolute;width:1px;white-space:nowrap;outline:0;left:0}.uxa-hidden[_nghost-%COMP%]   [dir=rtl][_ngcontent-%COMP%], .uxa-avatar-image.uxa-hidden[_ngcontent-%COMP%]   [dir=rtl][_ngcontent-%COMP%]{left:auto;right:0}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAvatarImageComponent, [{
    type: Component,
    args: [{
      standalone: true,
      selector: 'img[uxa-avatar-image]',
      template: '<ng-content />',
      changeDetection: ChangeDetectionStrategy.OnPush,
      providers: [{
        provide: UxaAvatarImageToken,
        useExisting: UxaAvatarImageComponent
      }],
      host: {
        '[class.uxa-hidden]': 'hidden()'
      },
      styles: [":host,.uxa-avatar-image{width:100%;height:100%;border-radius:50%}:host.uxa-hidden,.uxa-avatar-image.uxa-hidden{border:0;clip:rect(0 0 0 0);height:1px;margin-inline:-1px;margin-block:-1px;overflow:hidden;padding-inline:0;padding-block:0;position:absolute;width:1px;white-space:nowrap;outline:0;left:0}:host.uxa-hidden [dir=rtl],.uxa-avatar-image.uxa-hidden [dir=rtl]{left:auto;right:0}\n"]
    }]
  }], null, {
    onLoad: [{
      type: HostListener,
      args: ['load']
    }],
    onError: [{
      type: HostListener,
      args: ['error']
    }]
  });
})();
class UxaAvatarModule {
  static {
    this.ɵfac = function UxaAvatarModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAvatarModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaAvatarModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAvatarModule, [{
    type: NgModule,
    args: [{
      imports: [UxaAvatarComponent, UxaAvatarImageComponent],
      exports: [UxaAvatarComponent, UxaAvatarImageComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { UxaAvatarComponent, UxaAvatarImageComponent, UxaAvatarModule, provideAvatarConfig };
